import { get } from 'svelte/store';
import { MARKET_SLUGS } from '$lib/services/market/slugs';
import type { TMarket } from '$lib/services/market/types';
import { processUrl } from '$lib/services/wordpress/utils';
import {
    GAMES_TESTID,
    LOGIN_TESTID,
    MORE_MENU_TESTID,
    ODDS_TESTID,
    PROFILE_TESTID,
    REGISTER_TESTID,
    VERTICAL_TESTIDS,
    WALLET_TESTID
} from './constants';
import type { TWordpressShellAndFooterTranslations } from '$lib/services/wordpress/types';
import { getRoute, isActiveRoute } from '$src/lib/services/navigation';

import ROUTE_IDS, { type TRouteId } from '$generated/routeIds';
import { type TNotificationStore } from '$src/lib/stores/notifications';

const isRouteSlugActive = (routeSlugs: TRouteId[]) =>
    routeSlugs.some((slug) => get(isActiveRoute(slug)));

export const getItems = (
    t: TWordpressShellAndFooterTranslations,
    vertical: 'casino' | 'sports',
    market: TMarket,
    notifications: TNotificationStore
) => {
    const translations = t.shell.lis || t.shell.los;

    const isCasinoVertical = vertical === 'casino';
    const isSportsVertical = vertical === 'sports';

    const productHref = processUrl(
        isCasinoVertical ? translations?.casino_url : translations?.sports_url,
        market
    );

    const homeRouteSlugs = [
        ROUTE_IDS['GAMES_SEARCH'],
        ROUTE_IDS['HOME'],
        ROUTE_IDS['/slots'],
        ROUTE_IDS['/live-casino'],
        ROUTE_IDS['PROMOTIONS'],
        ROUTE_IDS['HALL_OF_FAME'],
        ROUTE_IDS['SPORTS']
    ];

    const walletRouteSlugs = [
        ROUTE_IDS.CASH_DEPOSIT,
        ROUTE_IDS.CASH_WITHDRAW,
        ROUTE_IDS.TRANSACTION_HISTORY,
        ROUTE_IDS.TRANSACTION_HISTORY_BETS
    ];
    const playerRouteSlugs = [
        ROUTE_IDS.PLAYER_DASHBOARD,
        ROUTE_IDS.DOCUMENTS_VERIFICATION,
        ROUTE_IDS.PLAYER_PLAY_OKAY_SETTINGS,
        ROUTE_IDS.PLAYER_SETTINGS
    ];

    const moreRouteSlugs = [ROUTE_IDS['MORE']];

    const isWalletActive = isRouteSlugActive(walletRouteSlugs);
    const isPlayerActive = isRouteSlugActive(playerRouteSlugs);
    const isHomeActive = isRouteSlugActive(homeRouteSlugs);
    const isMoreMenuActive = isRouteSlugActive(moreRouteSlugs);

    return {
        verticalNavItems: [
            {
                text: translations?.casino_label,
                href: processUrl(translations?.casino_url, market),
                selected: isCasinoVertical,
                buttonBarText: translations?.casino_label,
                testid: VERTICAL_TESTIDS.casino,
                enabled: true
            },
            {
                text: translations?.sports_label,
                href: processUrl(translations?.sports_url, market),
                selected: isSportsVertical,
                testid: VERTICAL_TESTIDS.sports,
                enabled: market.isSportsEnabled
            }
        ],
        wallet: {
            text: t.shell.lis?.wallet_label,
            href: getRoute(ROUTE_IDS.CASH_DEPOSIT),
            selected: isWalletActive,
            icon: 'wallet',
            iconActive: 'wallet',
            buttonBarText: t.shell.lis?.wallet_label,
            testid: WALLET_TESTID,
            hasNotification: notifications.transactions
        },
        profile: {
            text: t.shell.lis?.profile_label,
            href: getRoute(ROUTE_IDS.PLAYER_DASHBOARD),
            selected: isPlayerActive,
            icon: 'profile',
            iconActive: 'profile',
            buttonBarText: t.shell.lis?.profile_label,
            testid: PROFILE_TESTID,
            hasNotification: notifications.valuables || notifications.verification
        },

        moreMenuItem: {
            text: translations?.more_label,
            buttonBarText: translations?.more_label,
            href: processUrl(translations?.more_url, market),
            icon: 'mainMenu',
            iconActive: 'mainMenuActive',
            testid: MORE_MENU_TESTID,
            selected: isMoreMenuActive
        },

        productItem: {
            text: isCasinoVertical ? translations?.games_label : translations?.odds_label,
            buttonBarText: isCasinoVertical ? translations?.games_label : translations?.odds_label,
            href: productHref,
            icon: isCasinoVertical ? 'games' : 'odds',
            testid: isCasinoVertical ? GAMES_TESTID : ODDS_TESTID,
            selected: isHomeActive
        },

        loginItem: {
            text: t.shell.los?.login_label,
            testid: LOGIN_TESTID,
            href: processUrl(t.shell.los?.login_url, market)
        },
        registrationItem: {
            text: t.shell.los?.register_label,
            icon: market.slug === MARKET_SLUGS.SV ? 'bankID' : undefined,
            testid: REGISTER_TESTID,
            href: processUrl(t.shell.los?.register_url, market)
        }
    };
};
